import { Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { IssueListButton } from "components/Forms/IssueListButton";
import { PrefillWasteFieldsButton } from "components/Forms/PrefillWasteFieldsButton";
import { Issue, ProgramArea } from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformFacility";
import { useValidatingForm } from "hooks/useValidatingForm";
import { isEqual } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useFacilityInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useFacilityInputValidation";
import { hasCriticalIssues } from "util/forms";

import { FacilityDetailsInput } from "../FacilityProfile";
import { FacilityStateFieldsForm } from "../FacilityStateFieldsForm";
import { useFacility, useStateInformationIssues, useUpdateFacilityProfile } from "../useFacility";
import { STATE_INFORMATION_SECTION_ICON } from "./StateInformationSection";

interface EditStateInformationDialogProps {
  onClose: () => void;
  programArea: ProgramArea;
}

const LABELS: Record<ProgramArea, string> = {
  [ProgramArea.Epcra]: "Chemicals",
  [ProgramArea.Rcra]: "Waste",
};

export function EditStateInformationDialog({
  onClose,
  programArea,
}: EditStateInformationDialogProps) {
  const alerts = useAlerts();
  const { data } = useFacility();
  const facility = data?.facility;
  const [updateFacility, { loading: saving }] = useUpdateFacilityProfile();

  const allStateIssues = useStateInformationIssues();

  const filterByProgramArea = useCallback(
    <T extends { type?: ProgramArea | null }>(fields: T[]): T[] => {
      if (programArea === ProgramArea.Rcra) {
        return fields.filter((f) => f.type === ProgramArea.Rcra);
      }
      return fields.filter((f) => f.type !== ProgramArea.Rcra);
    },
    [programArea]
  );

  const programAreaFieldKeys = useMemo(
    () => new Set(filterByProgramArea(facility?.stateFields ?? []).map((sf) => sf.key)),
    [filterByProgramArea, facility?.stateFields]
  );
  const persistedIssues = useMemo(
    () => allStateIssues.filter((issue) => !!issue.key && programAreaFieldKeys.has(issue.key)),
    [allStateIssues, programAreaFieldKeys]
  );

  const defaultValues = useMemo<FacilityDetailsInput>(
    () => ({
      name: facility?.name ?? "",
      state: facility?.state ?? null,
      streetAddress1: facility?.streetAddress1 ?? null,
      streetAddress2: facility?.streetAddress2 ?? null,
      city: facility?.city ?? null,
      zip: facility?.zip ?? null,
      country: facility?.country ?? null,
      county: facility?.county ?? null,
      latitude: facility?.latitude ?? null,
      longitude: facility?.longitude ?? null,
      phone: facility?.phone ?? null,
      phone24Hour: facility?.phone24Hour ?? null,
      stateFields: filterByProgramArea(facility?.stateFields ?? []).map((sf) => ({
        ...sf,
        value: sf.value ?? null,
      })),
    }),
    [facility, filterByProgramArea]
  );

  const validator = useFacilityInputValidation({
    facilityId: facility?.id ?? "",
    reportId: undefined,
    pick: ["stateFields"],
  });

  const formValidator = useCallback(
    async (input: FacilityDetailsInput): Promise<Issue[]> => {
      const result = await validator(input);
      return result.filter((issue) => !!issue.key && programAreaFieldKeys.has(issue.key));
    },
    [validator, programAreaFieldKeys]
  );

  const form = useValidatingForm<FacilityDetailsInput>(
    defaultValues,
    persistedIssues,
    formValidator
  );
  const { handleSubmit, issues, watch } = form;

  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const currentValues = form.watch();
  const hasUnsavedChanges = useMemo(() => {
    const normalizeValue = (val: unknown): unknown => {
      if (val === "" || val === undefined) return null;
      if (Array.isArray(val)) return val.map(normalizeValue);
      if (val !== null && typeof val === "object") {
        return Object.fromEntries(Object.entries(val).map(([k, v]) => [k, normalizeValue(v)]));
      }
      return val;
    };
    return !isEqual(normalizeValue(currentValues), normalizeValue(defaultValues));
  }, [currentValues, defaultValues]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const stateFields = watch("stateFields");

  const onSubmit = useCallback(
    async (formData: FacilityDetailsInput) => {
      if (!facility) return;

      const input = transform(formData);

      try {
        await updateFacility({
          variables: {
            id: facility.id,
            facility: {
              stateFields: input.stateFields,
            },
          },
        });
        alerts.success("State information saved successfully.");
        onClose();
      } catch (err) {
        alerts.error("Error saving state information.");
        console.info("Error saving state information.", err);
      }
    },
    [facility, updateFacility, alerts, onClose]
  );

  const isCA = facility?.state === "CA";

  return (
    <>
      <Dialog
        open
        title={
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h6">Edit</Typography>
            <Typography variant="h6">{STATE_INFORMATION_SECTION_ICON}</Typography>
            <Typography variant="h6">State Information ({LABELS[programArea]})</Typography>
          </Stack>
        }
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        primaryActionButtonProps={{
          loading: saving,
          disabled: !hasUnsavedChanges || hasCriticalIssues(issues),
          onClick: () => handleSubmit(onSubmit)(),
        }}
        additionalPrimaryActionElements={[
          <IssueListButton issues={issues} key="issue-list-button" />,
        ]}
        secondaryActionElements={[
          // Rendered in the footer's left slot, outside the body's FormProvider,
          // so it gets its own provider bound to the same form. Returns null when
          // there are no prefillable fields (e.g. the Chemicals program area).
          <FormProvider {...form} key="prefill-waste-fields">
            <PrefillWasteFieldsButton
              fields={stateFields ?? []}
              namePrefix="stateFields"
              facilityId={facility?.id}
            />
          </FormProvider>,
        ]}
      >
        <FormProvider {...form}>
          <form>
            {isCA ? (
              <Typography>
                Because this facility is in California, the California HMBP questions will appear in
                the facility&apos;s Tier II report in Chemicals.
              </Typography>
            ) : (
              <FacilityStateFieldsForm
                jurisdictions={
                  [facility?.state, facility?.lepc?.id, facility?.fireDepartment?.id].filter(
                    Boolean
                  ) as string[]
                }
                context="facility"
                fields={stateFields ?? []}
                namePrefix="stateFields"
                programArea={programArea}
              />
            )}
          </form>
        </FormProvider>
      </Dialog>
      <ConfirmDialog
        open={showConfirmClose}
        onClose={() => setShowConfirmClose(false)}
        onConfirm={onClose}
        header="You have unsaved changes. Are you sure you want to close?"
        confirmText="Close without saving"
      />
    </>
  );
}
